import React, { useRef, useState } from 'react';
import { Tooltip, Empty, Popover } from 'antd';
import { SearchOutlined, CaretUpOutlined, CaretDownOutlined, CalendarOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { CommunicationsHistory, HistoricCommunication, CommunicationsHistoryForm, MasksConfig } from 'types';
import { ColumnsType } from 'antd/es/table';

import { historicCommunicationActions } from 'common/config/store';
import { isEspagne } from 'common/config/env';
import { useIntl } from 'common/config/i18n';
import StyledTable from 'components/organisms/styled-table';
import CommunicationHistoryModal from 'components/organisms/communication-history-modal';
import { FilterOrderParams } from 'pages/historisation';
import PaginationBlock from 'components/molecules/pagination';
import { OdsSummaryContent } from 'components/organisms/target-customer-stations/utils';
import { useTheme } from 'common/styling/theme';

const TABLE_ROW_LIGHT = 'table-row-light';
const TABLE_ROW_DARK = 'table-row-dark';

interface Props {
  data?: HistoricCommunication;
  filterValues?: CommunicationsHistoryForm;
  channels?: MasksConfig;
  filterOrder: FilterOrderParams;
  setFilterOrder: ({ name, order }: FilterOrderParams) => void;
  modalVisible: boolean;
  setModalVisible: (visible: boolean) => void;
}

type CommunicationsHistoryWithKey = CommunicationsHistory & { key: string };

interface ColumnType extends Omit<ColumnsType<CommunicationsHistoryWithKey>[number], 'children' | 'dataIndex'> {
  display?: boolean;
  dataIndex?: string | number | readonly (string | number)[];
  children?: ColumnType[];
}

const HistorisationList: React.FC<Props> = ({
  data,
  filterValues,
  channels,
  filterOrder,
  setFilterOrder,
  modalVisible,
  setModalVisible,
}) => {
  const { t } = useIntl();
  const dispatch = useDispatch();
  const { theme } = useTheme();

  const [record, setRecord] = useState<CommunicationsHistoryWithKey | null>(null);
  const previousId = useRef<number | undefined>(undefined);
  const rowColor = useRef<string>(TABLE_ROW_LIGHT);

  const rowClassName = (record: CommunicationsHistoryWithKey) => {
    if (record.communicationID !== previousId.current) {
      rowColor.current = rowColor.current === TABLE_ROW_LIGHT ? TABLE_ROW_DARK : TABLE_ROW_LIGHT;
      previousId.current = record.communicationID;
    }
    return rowColor.current;
  };

  const filterList = (
    parameterToSort: FilterOrderParams['name'],
    ascDesc: FilterOrderParams['order'],
    page?: number,
    pageSize?: number
  ) => {
    const isPaginationSearch = !!(page && pageSize);
    const order =
      filterOrder?.order && filterOrder?.name === parameterToSort
        ? filterOrder?.order === 'DESC'
          ? 'ASC'
          : 'DESC'
        : ascDesc;
    dispatch(
      historicCommunicationActions.fetchHistoricCommunication({
        ...filterValues,
        pageNumber: page || 1,
        maxResultPage: pageSize || 20,
        parameterToSort,
        ascDesc: isPaginationSearch ? ascDesc : order,
        communicationStartDate: filterValues?.communicationStartDate
          ? moment(filterValues?.communicationStartDate).format('YYYY-MM-DD')
          : undefined,
        communicationEndDate: filterValues?.communicationEndDate
          ? moment(filterValues?.communicationEndDate).format('YYYY-MM-DD')
          : undefined,
        departureTime: filterValues?.departureTime
          ? moment(filterValues?.departureTime).format('YYYY-MM-DD')
          : undefined,
        arrivalTime: filterValues?.arrivalTime ? moment(filterValues?.arrivalTime).format('YYYY-MM-DD') : undefined,
      })
    );
    setFilterOrder({ name: parameterToSort, order: isPaginationSearch ? ascDesc : order });
  };

  const columns = [
    {
      title: (
        <div
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={-1}
          onKeyDown={() => {
            // do nothing.
          }}
          onClick={() => {
            filterList('SentDateTime', 'DESC');
          }}
        >
          {t('table', 'CHART_DATE_TIME_SENDING')}
          {filterOrder.name === 'SentDateTime' && filterOrder?.order === 'DESC' && (
            <div>
              <CaretDownOutlined />
            </div>
          )}
          {filterOrder.name === 'SentDateTime' && filterOrder?.order === 'ASC' && (
            <div>
              <CaretUpOutlined />
            </div>
          )}
        </div>
      ),
      render: (record: CommunicationsHistoryWithKey) => (
        <div>
          {moment(record.sentDateTime).format('DD/MM/YYYY HH:mm')}
          {record?.isDifferedCommunication && (
            <CalendarOutlined style={{ position: 'absolute', bottom: 10, right: 10 }} />
          )}
        </div>
      ),
      key: 'Date',
    },
    {
      title: (
        <div
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={-1}
          onKeyDown={() => {
            // do nothing.
          }}
          onClick={() => {
            filterList('ProcessName', 'DESC');
          }}
        >
          {t('table', 'CHART_PROCESS')}
          {filterOrder.name === 'ProcessName' && filterOrder?.order === 'DESC' && (
            <div>
              <CaretDownOutlined />
            </div>
          )}
          {filterOrder.name === 'ProcessName' && filterOrder?.order === 'ASC' && (
            <div>
              <CaretUpOutlined />
            </div>
          )}
        </div>
      ),
      dataIndex: 'processName',
      key: 'Nature',
    },
    {
      title: (
        <div
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={-1}
          onKeyDown={() => {
            // do nothing.
          }}
          onClick={() => {
            filterList('SituationName', 'ASC');
          }}
        >
          {t('table', 'CHART_SITUATION')}
          {filterOrder.name === 'SituationName' && filterOrder?.order === 'DESC' && (
            <div>
              <CaretDownOutlined />
            </div>
          )}
          {filterOrder.name === 'SituationName' && filterOrder?.order === 'ASC' && (
            <div>
              <CaretUpOutlined />
            </div>
          )}
        </div>
      ),
      dataIndex: 'situationName',
      key: 'Nature',
    },
    {
      title: (
        <div
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={-1}
          onKeyDown={() => {
            // do nothing.
          }}
          onClick={() => {
            filterList('Sender', 'ASC');
          }}
        >
          {t('table', 'CHART_TRANSMITTER')}
          {filterOrder.name === 'Sender' && filterOrder?.order === 'DESC' && (
            <div>
              <CaretDownOutlined />
            </div>
          )}
          {filterOrder.name === 'Sender' && filterOrder?.order === 'ASC' && (
            <div>
              <CaretUpOutlined />
            </div>
          )}
        </div>
      ),
      dataIndex: 'sender',
      key: 'Nature',
    },
    {
      title: (
        <div
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={-1}
          onKeyDown={() => {
            // do nothing.
          }}
          onClick={() => {
            filterList('ServiceName', 'ASC');
          }}
        >
          {t('table', 'CHART_TRAIN')}
          {filterOrder.name === 'ServiceName' && filterOrder?.order === 'DESC' && (
            <div>
              <CaretDownOutlined />
            </div>
          )}
          {filterOrder.name === 'ServiceName' && filterOrder?.order === 'ASC' && (
            <div>
              <CaretUpOutlined />
            </div>
          )}
        </div>
      ),
      dataIndex: 'serviceName',
      key: 'Train',
    },
    {
      title: (
        <div
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={-1}
          onKeyDown={() => {
            // do nothing.
          }}
          onClick={() => {
            filterList('ServiceDate', 'DESC');
          }}
        >
          {t('table', 'CHART_TRAIN_DATE')}
          {filterOrder.name === 'ServiceDate' && filterOrder?.order === 'DESC' && (
            <div>
              <CaretDownOutlined />
            </div>
          )}
          {filterOrder.name === 'ServiceDate' && filterOrder?.order === 'ASC' && (
            <div>
              <CaretUpOutlined />
            </div>
          )}
        </div>
      ),
      render: (record: CommunicationsHistoryWithKey) => <div>{moment(record.serviceDate).format('DD/MM/YYYY')}</div>,
      key: 'TrainDate',
    },
    {
      title: t('table', 'CHART_TARGETING'),
      dataIndex: 'lastComType',
      key: 'Gare',
      children: [
        {
          title: (
            <div
              style={{ cursor: 'pointer' }}
              role="button"
              tabIndex={-1}
              onKeyDown={() => {
                // do nothing.
              }}
              onClick={() => {
                filterList('CustomerSelection', 'DESC');
              }}
            >
              {t('table', 'CHART_PASSENGERS')}
              {filterOrder.name === 'CustomerSelection' && filterOrder?.order === 'DESC' && (
                <div>
                  <CaretDownOutlined />
                </div>
              )}
              {filterOrder.name === 'CustomerSelection' && filterOrder?.order === 'ASC' && (
                <div>
                  <CaretUpOutlined />
                </div>
              )}
            </div>
          ),
          render: (record: CommunicationsHistoryWithKey) => <div>{record?.customerSelection}</div>,
        },
        {
          title: (
            <div
              style={{ cursor: 'pointer' }}
              role="button"
              tabIndex={-1}
              onKeyDown={() => {
                // do nothing.
              }}
              onClick={() => {
                filterList('Station', 'ASC');
              }}
            >
              {t('table', 'CHART_STATIONS')}
              {filterOrder.name === 'Station' && filterOrder?.order === 'DESC' && (
                <div>
                  <CaretDownOutlined />
                </div>
              )}
              {filterOrder.name === 'Station' && filterOrder?.order === 'ASC' && (
                <div>
                  <CaretUpOutlined />
                </div>
              )}
            </div>
          ),
          render: (record: CommunicationsHistoryWithKey) => (
            <Tooltip title={record?.station?.name} placement="top">
              <div>{record?.station?.shortCode}</div>
            </Tooltip>
          ),
          key: 'Nature',
        },
        {
          title: t('table', 'CHART_EXCLUSION'),
          render: (record: CommunicationsHistoryWithKey) => {
            let content: string[][] | string;
            if (record.overviewOD?.length) {
              content = record.overviewOD?.map((item) => item.split(' ; '));
            } else if (record?.excludedStations.length > 0) {
              content = record?.excludedStations.map((station) => station.shortCode).join(', ');
            } else {
              content = '';
            }
            if (content) {
              return (
                <Popover
                  placement="top"
                  color={theme.colors.common.black}
                  content={<OdsSummaryContent noHeader color={theme.colors.common.white} content={content} />}
                  trigger="hover"
                >
                  {t('table', 'CHART_YES')}
                </Popover>
              );
            }
          },
        },
        {
          title: t('table', 'CHART_CARRIAGE'),
          dataIndex: 'selectedCarriages',
        },
        {
          title: t('table', 'CHART_OTHER'),
          render: (record: CommunicationsHistoryWithKey) =>
            record?.other && (
              <Tooltip title={record.other} placement="right">
                <div>{t('table', 'CHART_YES')}</div>
              </Tooltip>
            ),
        },
      ],
    },
    {
      title: t('table', 'CHART_CHANNEL'),
      children: [
        {
          title: t('table', 'CHART_EMAIL'),
          render: (record: CommunicationsHistoryWithKey) => <div>{record?.emailStatus}</div>,
          key: 'Email',
          display: channels?.mail,
        },
        {
          title: t('table', 'CHART_WHATSAPP'),
          render: (record: CommunicationsHistoryWithKey) => <div>{record?.whatsAppStatus}</div>,
          key: 'Whatsapp',
          display: channels?.whatsApp,
        },
        {
          title: 'SMS',
          render: (record: CommunicationsHistoryWithKey) => <div>{record?.smsStatus}</div>,
          key: 'SMS',
          display: channels?.sms,
        },
      ],
    },
    {
      title: t('table', 'CHART_DELAY'),
      render: (record: CommunicationsHistoryWithKey) => <div>{record?.delay}</div>,
      key: 'Delay',
      width: 100,
    },
    {
      title: t('table', 'CHART_COMPENSATION'),
      children: [
        {
          title: (
            <div
              style={{ cursor: 'pointer' }}
              role="button"
              tabIndex={-1}
              onKeyDown={() => {
                // do nothing.
              }}
              onClick={() => {
                filterList('Compensation', 'DESC');
              }}
            >
              {'%'}
              {filterOrder.name === 'Compensation' && filterOrder?.order === 'DESC' && (
                <div>
                  <CaretDownOutlined />
                </div>
              )}
              {filterOrder.name === 'Compensation' && filterOrder?.order === 'ASC' && (
                <div>
                  <CaretUpOutlined />
                </div>
              )}
            </div>
          ),
          render: (record: CommunicationsHistoryWithKey) => record?.compensation && <div>{record?.compensation}</div>,
        },
        {
          title: '¿Seleccionado?',
          display: isEspagne,
          render: (record: CommunicationsHistoryWithKey) =>
            // TODO: temporrary solution (remove the condition when handled by MDW)
            (record?.compensation || (!record?.compensation && record?.compensationSelected === 'Si')) && (
              <div>{record?.compensationSelected}</div>
            ),
        },
      ],
    },
    {
      title: t('table', 'CHART_LAST_SEND_DETAILS'),
      render: (record: CommunicationsHistoryWithKey) => (
        <SearchOutlined
          onClick={() => {
            setRecord(record);
            setModalVisible(true);
          }}
          style={{ color: '#00ACD0', fontSize: 20 }}
        />
      ),
      key: 'Nature',
    },
  ];

  const filteredColumns = columns
    .map((column: ColumnType) => {
      if (column.children) {
        return { ...column, children: column.children.filter((child) => child.display !== false) };
      } else if (column.display !== false) {
        return column;
      }
    })
    .filter((column) => {
      return column !== undefined;
    });

  const historicCommDataSource: CommunicationsHistoryWithKey[] = data?.result
    ? data?.result?.map((item, index) => ({
        key: `${item.serviceName}-${index}`,
        ...item,
      }))
    : [];

  return (
    <>
      {record && (
        <CommunicationHistoryModal onClose={() => setModalVisible(false)} isOpen={modalVisible} data={record} />
      )}
      <>
        <PaginationBlock
          data={{ pageNumber: data?.pageNumber, maxPages: data?.maxPages, maxResultPage: data?.maxResultPage }}
          onChange={(page: number, pageSize?: number) =>
            filterList(filterOrder.name || 'SentDateTime', filterOrder.order || 'DESC', page, pageSize)
          }
        />
        <StyledTable
          dataSource={historicCommDataSource}
          columns={filteredColumns as typeof columns}
          rowClassName={(record) => rowClassName(record as CommunicationsHistoryWithKey)}
          pagination={false}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('empty')} /> }}
        />
        <PaginationBlock
          data={{ pageNumber: data?.pageNumber, maxPages: data?.maxPages, maxResultPage: data?.maxResultPage }}
          onChange={(page: number, pageSize?: number) =>
            filterList(filterOrder.name || 'SentDateTime', filterOrder.order || 'DESC', page, pageSize)
          }
        />
      </>
    </>
  );
};

export default HistorisationList;
